import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  AlertCircle,
  Bell,
  BellOff,
  ChevronRight,
  Cpu,
  Gauge,
  Lock,
  LockOpen,
  Radio,
  Sparkles,
  Waves,
  Zap,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { usePulseStore, usePrideStore } from "@/lib/pulseStore";
import { chimeEngine } from "@/lib/pulseAudio";

const STAGES = [
  { key: "INTAKE", label: "Intake", color: "#38bdf8" },
  { key: "AGENT_TRIAGE", label: "Agent Triage", color: "#a78bfa" },
  { key: "AI_VERIFY", label: "AI Verify", color: "#22d3ee" },
  { key: "HUMAN_HANDOFF_READY", label: "Handoff Ready", color: "#f59e0b" },
  { key: "LO_ENGAGED", label: "LO Engaged", color: "#10b981" },
  { key: "CLOSED", label: "Closed", color: "#8b5cf6" },
];

const ROLE_META = {
  lionesses: { color: "#f97316", label: "Lionesses", tag: "Chief of Staff" },
  lion: { color: "#eab308", label: "Lion", tag: "Underwriting Staging" },
  elephant: { color: "#94a3b8", label: "Elephant", tag: "Document Parsing" },
  tiger: { color: "#fb923c", label: "Tiger", tag: "Purchase Power" },
  gazelle: { color: "#22d3ee", label: "Gazelle", tag: "Readiness Coach" },
  monkey: { color: "#a3e635", label: "Monkey", tag: "Engagement Tokens" },
  cheetah: { color: "#f472b6", label: "Cheetah", tag: "Media Pipeline" },
  panther: { color: "#8b5cf6", label: "Panther", tag: "Localization" },
};

export default function Pulse() {
  const [token, setToken] = useState(() => localStorage.getItem("pulse_admin_token") || "");
  const [entered, setEntered] = useState(() => Boolean(localStorage.getItem("pulse_admin_token")));
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Nexus Pipeline Live Pulse";
  }, []);

  const enter = async () => {
    if (!token.trim()) {
      setError("Enter an admin token to unlock the pulse.");
      return;
    }
    localStorage.setItem("pulse_admin_token", token.trim());
    setError("");
    setEntered(true);
    await chimeEngine.ensureActive();
    chimeEngine.playTick(0.2);
  };

  if (!entered) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 backdrop-blur p-8 shadow-[0_0_60px_-20px_rgba(16,185,129,0.35)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.3em] text-emerald-300">NEXUS PIPELINE</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">Live Pulse</h1>
          <p className="text-sm text-slate-400 mt-2 mb-6">
            Ambient desktop command hub for the DW pipeline. Enter your operator token.
          </p>
          <input
            data-testid="pulse-token-input"
            className="w-full rounded-lg bg-slate-950 border border-slate-800 focus:border-emerald-500 outline-none px-3 py-2.5 text-sm text-slate-100 font-mono tracking-wide"
            placeholder="dwig-admin-XXXX"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && enter()}
          />
          {error ? <p className="text-xs text-rose-400 mt-2">{error}</p> : null}
          <button
            data-testid="pulse-enter-btn"
            className="w-full mt-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-semibold py-2.5 text-sm transition"
            onClick={enter}
          >
            Engage Live Pulse →
          </button>
        </div>
      </div>
    );
  }

  return <PulseDashboard />;
}

function PulseDashboard() {
  const {
    connect,
    disconnect,
    connected,
    latencyMs,
    handshakes,
    streamHistory,
    handoffQueue,
    stageCounts,
    totals,
    unreadAlerts,
    soundEnabled,
    wakeLockActive,
    toggleSound,
    toggleWakeLock,
    acknowledgeHandoff,
    seedDemoEvent,
  } = usePulseStore();

  const prideStore = usePrideStore();

  useEffect(() => {
    connect();
    prideStore.refresh();
    const timer = setInterval(() => prideStore.refresh(), 3000);
    return () => {
      clearInterval(timer);
      disconnect();
    };
  }, []);

  const chartData = useMemo(() => streamHistory.map((p) => ({
    ...p,
    label: new Date(p.timestamp).toLocaleTimeString([], { minute: "2-digit", second: "2-digit" }),
  })), [streamHistory]);

  const totalActive = totals.active ?? 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Top Telemetry Bar */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur px-6 py-3 flex items-center gap-5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-rose-500"}`} />
          <span className="text-[10px] font-bold tracking-[0.25em] text-slate-300">
            NEXUS PIPELINE · LIVE PULSE
          </span>
        </div>
        <div className="h-4 w-px bg-slate-800" />
        <TelemetryChip
          testId="tele-wake"
          onClick={toggleWakeLock}
          icon={wakeLockActive ? <Lock size={13} /> : <LockOpen size={13} />}
          label="WAKE LOCK"
          value={wakeLockActive ? "ON" : "OFF"}
          accent={wakeLockActive ? "text-emerald-300" : "text-slate-500"}
        />
        <TelemetryChip
          testId="tele-audio"
          onClick={toggleSound}
          icon={soundEnabled ? <Bell size={13} /> : <BellOff size={13} />}
          label="CHIME"
          value={soundEnabled ? "ENABLED" : "MUTED"}
          accent={soundEnabled ? "text-emerald-300" : "text-slate-500"}
        />
        <TelemetryChip
          icon={<Gauge size={13} />}
          label="LATENCY"
          value={`${latencyMs}ms`}
          accent={latencyMs < 200 ? "text-emerald-300" : latencyMs < 800 ? "text-amber-300" : "text-rose-400"}
        />
        <TelemetryChip
          icon={<Radio size={13} />}
          label="HANDSHAKES"
          value={handshakes.toString()}
          accent="text-cyan-300"
        />
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="seed-demo-btn"
            onClick={seedDemoEvent}
            className="text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded border border-slate-700 hover:border-emerald-500 hover:text-emerald-300 text-slate-400 transition"
          >
            SIMULATE TICK
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="p-6 grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Left column — 60% */}
        <div className="xl:col-span-3 flex flex-col gap-5">
          <HeroCard total={totalActive} closed={totals.closed} handoffReady={totals.handoffReady} />
          <StreamChart data={chartData} />
          <StageDistributionRow stageCounts={stageCounts} />
        </div>

        {/* Right column — 40% */}
        <div className="xl:col-span-2 flex flex-col gap-5">
          <HandoffRadar
            alerts={unreadAlerts}
            queue={handoffQueue}
            onAck={acknowledgeHandoff}
          />
          <PrideMissionControl store={prideStore} />
        </div>
      </div>
    </div>
  );
}

function TelemetryChip({ icon, label, value, accent = "text-slate-300", onClick, testId }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 text-[10px] font-mono tracking-wide ${onClick ? "hover:opacity-80 transition" : ""}`}
    >
      <span className={`${accent} flex items-center gap-1`}>{icon}<span className="uppercase font-bold tracking-[0.2em]">{label}</span></span>
      <span className={`${accent} font-bold`}>{value}</span>
    </Comp>
  );
}

function HeroCard({ total, closed, handoffReady }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-[0.3em] text-slate-500">ACTIVE PIPELINE</span>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[9px] font-bold tracking-[0.2em] text-emerald-300">STREAMING</span>
        </div>
      </div>
      <div className="mt-4 flex items-end gap-4">
        <div className="text-6xl font-black tracking-tighter tabular-nums">{total}</div>
        <div className="pb-2 flex flex-col text-[11px] text-slate-400">
          <span><span className="text-emerald-300 font-bold tabular-nums">{closed}</span> closed</span>
          <span><span className="text-amber-300 font-bold tabular-nums">{handoffReady}</span> in queue</span>
        </div>
      </div>
    </div>
  );
}

function StreamChart({ data }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Waves size={16} className="text-emerald-300" />
          <h2 className="text-sm font-bold tracking-wide">Pipeline Velocity · Rolling 15m</h2>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-bold tracking-[0.2em] text-slate-400">
          <LegendDot color="#10b981" label="READY" />
          <LegendDot color="#f59e0b" label="AI PROC" />
          <LegendDot color="#38bdf8" label="INTAKE" />
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 6, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="gradReady" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradIntake" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" />
            <XAxis dataKey="label" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} interval={2} />
            <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#94a3b8", fontSize: 11 }}
            />
            <Area type="monotone" dataKey="readyForHandoffCount" stroke="#10b981" strokeWidth={2} fill="url(#gradReady)" />
            <Line type="monotone" dataKey="aiProcessingCount" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="3 3" />
            <Area type="monotone" dataKey="intakeVelocity" stroke="#38bdf8" strokeWidth={1.5} fill="url(#gradIntake)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function StageDistributionRow({ stageCounts }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={16} className="text-cyan-300" />
        <h2 className="text-sm font-bold tracking-wide">Stage Distribution</h2>
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
        {STAGES.map((s, i) => {
          const count = stageCounts[s.key] ?? 0;
          return (
            <React.Fragment key={s.key}>
              <div
                data-testid={`stage-${s.key}`}
                className="flex-1 min-w-[130px] rounded-xl border p-3"
                style={{ borderColor: s.color + "44", background: s.color + "0F" }}
              >
                <div className="flex items-center gap-1.5 text-[9px] font-bold tracking-[0.2em] text-slate-400">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                  {s.label.toUpperCase()}
                </div>
                <div className="mt-1.5 text-3xl font-black tabular-nums" style={{ color: s.color }}>
                  {count}
                </div>
              </div>
              {i < STAGES.length - 1 ? (
                <ChevronRight size={14} className="self-center text-slate-700 shrink-0" />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function HandoffRadar({ alerts, queue, onAck }) {
  const latest = alerts[0];
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-300" />
          <h2 className="text-sm font-bold tracking-wide">Live Handoff Radar</h2>
        </div>
        <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500">
          {queue.length} WAITING
        </div>
      </div>

      <AnimatePresence>
        {latest ? (
          <motion.div
            key={latest.id}
            data-testid="handoff-flash"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="rounded-xl border-2 border-emerald-500 bg-emerald-500/10 p-4 mb-3 animate-pulse ring-2 ring-emerald-500/40"
          >
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-emerald-300" />
              <span className="text-[10px] font-bold tracking-[0.25em] text-emerald-300">READY FOR HUMAN SIGN-OFF</span>
            </div>
            <div className="mt-2 text-lg font-black">{latest.applicantName}</div>
            <div className="text-[11px] text-slate-400 mt-1">
              {latest.sector} · DTI <span className="text-emerald-300 font-bold tabular-nums">{latest.dti}%</span>
              {latest.ltv ? <> · LTV <span className="text-emerald-300 font-bold tabular-nums">{latest.ltv}%</span></> : null}
            </div>
            <button
              data-testid="handoff-accept-btn"
              onClick={() => onAck(latest.leadId)}
              className="mt-3 w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[11px] tracking-[0.15em] py-2 flex items-center justify-center gap-1.5 transition"
            >
              <Zap size={12} /> ACCEPT &amp; OPEN LOAN FILE
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 mb-2 mt-4">QUEUE</div>
      <div className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-1">
        {queue.length === 0 ? (
          <div className="text-xs text-slate-500 py-6 text-center border border-dashed border-slate-800 rounded-lg">
            No handoffs pending.
          </div>
        ) : (
          queue.map((l) => (
            <div
              key={l.id}
              data-testid={`queue-${l.id}`}
              className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/60 p-3 hover:border-emerald-500/60 transition"
            >
              <UrgencyDot level={l.urgency} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{l.fullName}</div>
                <div className="text-[10px] text-slate-500">
                  {l.sector} · DTI <span className="text-slate-300 tabular-nums">{l.dti}%</span>
                </div>
              </div>
              <button
                data-testid={`queue-take-${l.id}`}
                onClick={() => onAck(l.id)}
                className="text-[10px] font-bold tracking-[0.15em] px-2.5 py-1 rounded border border-slate-700 hover:border-emerald-500 hover:text-emerald-300 text-slate-300 transition"
              >
                TAKE →
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function UrgencyDot({ level }) {
  const colors = { IMMEDIATE: "#ef4444", PRIORITY: "#f59e0b", STANDARD: "#38bdf8" };
  const c = colors[level] || "#64748b";
  return (
    <div className="relative">
      <div className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
      {level === "IMMEDIATE" ? (
        <div className="absolute inset-0 rounded-full animate-ping" style={{ background: c, opacity: 0.6 }} />
      ) : null}
    </div>
  );
}

function PrideMissionControl({ store }) {
  const [dispatchInput, setDispatchInput] = useState("APPLICATION_NEAR_MISS");
  const [lastPlan, setLastPlan] = useState(null);
  const PRESETS = [
    { key: "DOC_UPLOADED", label: "Doc uploaded" },
    { key: "APPLICATION_NEAR_MISS", label: "Near-miss client" },
    { key: "HISPANIC_CAMPAIGN_REQUESTED", label: "Hispanic reel" },
    { key: "MONTHLY_TOKEN_REFRESH", label: "Token refresh" },
    { key: "PRE_APPROVAL_LOCKED", label: "Final approval" },
  ];

  const dispatch = async (evtType) => {
    const plan = await store.dispatch(evtType, {});
    if (plan) setLastPlan(plan);
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-fuchsia-300" />
          <h2 className="text-sm font-bold tracking-wide">Mission Control · The Pride</h2>
        </div>
        <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500">
          {(() => {
            const now = Date.now();
            const activeAgents = new Set(
              (store.events || [])
                .filter((e) => e.status === "active" && now - new Date(e.created_at).getTime() < 5000)
                .map((e) => e.role)
            );
            return `${activeAgents.size}/8 ACTIVE`;
          })()}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {store.roles.map((r) => (
          <RoleCell key={r.role} role={r} />
        ))}
      </div>

      <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 mb-2">DISPATCH EVENT</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            data-testid={`pride-preset-${p.key}`}
            onClick={() => setDispatchInput(p.key)}
            className={`text-[10px] font-bold tracking-wide px-2 py-1 rounded border transition ${
              dispatchInput === p.key
                ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid="pride-event-input"
          value={dispatchInput}
          onChange={(e) => setDispatchInput(e.target.value)}
          className="flex-1 rounded-lg bg-slate-950 border border-slate-800 outline-none focus:border-emerald-500 px-2.5 py-1.5 text-[11px] font-mono text-slate-100"
        />
        <button
          data-testid="pride-dispatch-btn"
          onClick={() => dispatch(dispatchInput)}
          disabled={store.running}
          className="rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-bold text-[10px] tracking-[0.2em] px-3 py-1.5 flex items-center gap-1.5"
        >
          {store.running ? "…" : <><Cpu size={11} /> DISPATCH</>}
        </button>
      </div>

      {lastPlan ? (
        <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/70 p-3" data-testid="pride-last-plan">
          <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 mb-1">LAST TRIAGE</div>
          <div className="text-xs font-bold text-emerald-300 mb-1">{lastPlan.pipeline}</div>
          <div className="text-[11px] text-slate-400 leading-snug mb-2">{lastPlan.triage_summary}</div>
          <div className="flex flex-wrap gap-1">
            {(lastPlan.results || []).map((r) => (
              <span
                key={r.agent + r.started_at}
                className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded"
                style={{
                  background: (ROLE_META[r.agent]?.color || "#64748b") + "22",
                  color: ROLE_META[r.agent]?.color || "#94a3b8",
                }}
              >
                {ROLE_META[r.agent]?.label || r.agent} · {r.task_type}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RoleCell({ role }) {
  const meta = ROLE_META[role.role] || { color: "#64748b", label: role.role };
  const active = role.status === "active";
  return (
    <div
      data-testid={`role-${role.role}`}
      className={`rounded-lg border p-2.5 flex flex-col gap-1 transition ${
        active ? "shadow-[0_0_20px_-8px_rgba(16,185,129,0.6)]" : ""
      }`}
      style={{
        borderColor: (active ? meta.color : "#1e293b"),
        background: active ? meta.color + "10" : "#0b1220",
      }}
    >
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${active ? "animate-pulse" : ""}`} style={{ background: meta.color }} />
        <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: meta.color }}>{meta.label}</span>
      </div>
      <div className="text-[10px] text-slate-400 leading-tight truncate">{role.current_task}</div>
    </div>
  );
}
